import { useEffect } from 'react';
import { isLite } from '../lib/devicePerf';
import { useDevice } from '../store/device';
import { useSettings } from '../store/settings';

/**
 * Sizes everything (clock, widgets, news, settings) by setting the root font size, which
 * all Tailwind rem units follow. Auto mode treats a screen whose shorter side is 1080px as
 * 1× (1080p in either orientation), so a 4K TV, a tablet or a portrait-mounted screen all
 * show the same proportions; the size multiplier applies on top.
 */
export function useDisplayScale(): void {
  const { scaleMode, scale } = useSettings((s) => s.display);
  const effects = useDevice((s) => s.effects);

  // Lite mode (TV sticks): styles.css swaps blur for solid panels when data-lite is set.
  useEffect(() => {
    const root = document.documentElement;
    if (isLite(effects)) root.dataset.lite = '1';
    else delete root.dataset.lite;
  }, [effects]);

  useEffect(() => {
    const root = document.documentElement;
    const apply = () => {
      // The shorter side, so a portrait screen (1080×1920) sizes like its landscape twin.
      const short = Math.min(window.innerWidth, window.innerHeight);
      const screen = scaleMode === 'auto' ? Math.max(short, 360) / 1080 : 1;
      // Never smaller than 12px (readability) or larger than 64px (sanity).
      const px = Math.min(64, Math.max(12, 16 * screen * scale));
      root.style.fontSize = `${px.toFixed(2)}px`;
    };
    apply();
    window.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      root.style.fontSize = '';
    };
  }, [scaleMode, scale]);
}
